import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { typeContextCourantToContextAppType, useAppContextStore } from '@/features/auth/context-app'
import { type NiveauPermission, type TypePermissionLike, TypePermissionMap } from '@/features/domaine/types/opco'
import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useAuthentificationStore } from '../../auth'
import { AuthAktoEtats, Utilisateur, UtilisateurBase } from '../../auth/authentification/modeles'

export const useAppStateStore = defineStore('app-state', () => {
  const storeAuth = useAuthentificationStore()
  const { utilisateur, etat } = storeToRefs(storeAuth)
  const appContext = useAppContextStore()
  const { contextCourant, contextesAutorises } = storeToRefs(appContext)

  const estUtilisateurAuthentifie = computed<boolean>(() =>
    utilisateur.value instanceof UtilisateurBase
  )

  const estAuthentificationEnCours = computed<boolean>(() =>
    ![AuthAktoEtats.NonAuthentifié, AuthAktoEtats.Authentifié].includes(etat.value)
  )

  const estUtilisateurInterne = computed<boolean>(() =>
    utilisateur.value?.estInterne === true
  )

  const estUtilisateurImpersonnalise = computed<boolean>(() =>
    utilisateur.value instanceof Utilisateur && utilisateur.value.estImpersonnalisé
  )

  const estAuthentifeAvecUnContexte = computed(
    () => estUtilisateurAuthentifie.value && contextesAutorises.value !== TypesContextesAutorisesEnum.Aucun,
  )

  function getPermissions(permission: TypePermissionLike): NiveauPermission | undefined {
    if (!utilisateur.value?.contextsApp || !contextCourant.value) return

    const typeContextApp = typeContextCourantToContextAppType(contextCourant.value)
    const permissions = utilisateur.value.contextsApp[typeContextApp]
    if (!permissions) return

    const typePermission = TypePermissionMap[permission]
    return permissions[typePermission]
  }

  const buildAutorise =
    (niveauxPermission: NiveauPermission[], permissionDefault: NiveauPermission = 'Aucun') =>
      (...typesPermissions: TypePermissionLike[]) =>
        typesPermissions
          .map(getPermissions)
          .some(permission =>
            niveauxPermission.includes(permission ?? permissionDefault)
          )

  /** Indique si l'utilisateur dispose au moins du niveau de permission */
  const estFeatureVisible = buildAutorise(['Aucun', 'Lecture', 'Ecriture'], 'NC')
  const autoriseLecture = buildAutorise(['Lecture', 'Ecriture'])
  const autoriseEcriture = buildAutorise(['Ecriture'])
  const estAdmin = computed<boolean>(() => autoriseEcriture('Administration'))

  const connexionAsync = () =>
    storeAuth.authentificationRequiseAsync('/', false)

  const inscriptionAsync = () =>
    storeAuth.authentificationRequiseAsync('/', true)

  return {
    estAdmin,
    utilisateur,
    connexionAsync,
    inscriptionAsync,
    deconnexionAsync: storeAuth.deconnexionAsync,
    estUtilisateurAuthentifie,
    estAuthentificationEnCours,
    estUtilisateurInterne,
    estUtilisateurImpersonnalise,
    estAuthentifeAvecUnContexte,
    contextCourant,
    contextesAutorises,
    autoriseEcriture,
    autoriseLecture,
    estFeatureVisible
  }
})

export type AppStateStore = ReturnType<typeof useAppStateStore>
